import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled } from "@mui/system";
import { useDispatch } from "react-redux";
import NewPatientModal from "../shared/NewPatientModal";
import { logout } from "../../store/slices/authSlice";
import { ViewerPath } from "../../api/Authentication";
import { Settings } from "@mui/icons-material";
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import AboutDevice from "../shared/AboutDevice";
import Typography from "@mui/material/Typography";
import { TabButton } from "../shared/TabButton";
import DownloadIcon from "@mui/icons-material/Download";
import Badge from "@mui/material/Badge";
import ActiveDownloadsModal from "../shared/ActiveDownloadsModal";
import { getActiveDownloadsCount, subscribe } from "../../utils/download-manager";

const ToolbarGroup = styled("div")({
  display: "flex",
  alignItems: "center",
});

const ToolbarSpacer = styled("div")({
  flexGrow: 1,
});

const StyledIconButton = styled(IconButton)({
  marginRight: "5px",
});

const TopToolbar = ({
  handleDrawerToggle,
  activeTab,
  setActiveTab,
  selectedPatient,
  setIframeUrl,
  openIframeViewer,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isNewPatientModalOpen, setIsNewPatientModalOpen] = useState(false);
  const toolbarRef = useRef(null);
  const [openSettings, setOpenSettings] = useState(false);
  const [openHelp, setOpenHelp] = useState(false);
  const settingsAnchorRef = useRef(null);
  const helpAnchorRef = useRef(null);
  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = useState(false);
  const [activeDownloadsCount, setActiveDownloadsCount] = useState(0);
  const [isActiveDownloadsModalOpen, setIsActiveDownloadsModalOpen] = useState(false);

  const updateToolbarWidth = () => {
    const toolbarWidth = toolbarRef.current?.clientWidth;
    if (toolbarWidth) {
      localStorage.setItem("toolbarWidth", toolbarWidth);
    }
  };

  useLayoutEffect(() => {
    updateToolbarWidth();
    window.addEventListener("resize", updateToolbarWidth);
    return () => window.removeEventListener("resize", updateToolbarWidth);
  }, []);

  // Subscribe to download updates
  useEffect(() => {
    // Get initial count
    setActiveDownloadsCount(getActiveDownloadsCount());
    
    // Subscribe to updates
    const unsubscribe = subscribe(() => {
      setActiveDownloadsCount(getActiveDownloadsCount());
    });
    
    return unsubscribe;
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleMenuClose();
  };

  const handleViewClick = () => {
    if (!selectedPatient?.uuid) return;

    const token = localStorage.getItem("token");
    const viewerUrl = `${ViewerPath}?token=${token}&patientId=${selectedPatient.uuid}&origin=iframe`;
    openIframeViewer(viewerUrl);
  };

  const handleToggleSettings = () => {
    setOpenSettings((prevOpen) => !prevOpen);
    setOpenHelp(false);
  };

  const handleCloseSettings = (event) => {
    if (
      settingsAnchorRef.current &&
      settingsAnchorRef.current.contains(event.target)
    ) {
      return;
    }
    setOpenSettings(false);
    setOpenHelp(false);
  };

  const handleOpenHelp = () => {
    setOpenHelp(true);
  };

  const handleCloseHelp = () => {
    setOpenHelp(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        color="primary"
        sx={{
          boxShadow: "0px 4px 6px 0px #00000040",
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: "#1F2735",
        }}
        ref={toolbarRef}
      >
        <Toolbar
          sx={{
            minHeight: "64px !important",
            maxHeight: "64px",
            height: "64px",
            paddingLeft: "32px !important",
            paddingRight: "32px !important",
          }}
        >
          <ToolbarGroup>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            {!isMobile && (
              <img
                src={require("../../assets/images/MOVU-logo_white-notext.png")}
                alt="Logo"
                style={{ width: "36px" }}
              />
            )}
          </ToolbarGroup>
          <ToolbarGroup
            style={{ marginLeft: isMobile ? 0 : "44px", display: "flex" }}
          >
            <TabButton
              variant={activeTab === "patients" ? "contained" : "outlined"}
              onClick={() => setActiveTab("patients")}
            >
              <PeopleAltOutlinedIcon />
              <Typography component="span">
                Patient List
              </Typography>
            </TabButton>
            <TabButton
              variant={activeTab === "view" ? "contained" : "outlined"}
              disabled={!selectedPatient}
              onClick={handleViewClick}
            >
              <ImageOutlinedIcon />
              <Typography component="span">
                Viewer
              </Typography>
            </TabButton>
          </ToolbarGroup>
          <ToolbarSpacer />
          {activeTab === "view" && (
            <ToolbarGroup
              style={{
                marginLeft: isMobile ? 0 : "32px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>
                {selectedPatient &&
                  `${
                    selectedPatient.last_name.charAt(0).toUpperCase() +
                    selectedPatient.last_name.slice(1)
                  }, ${
                    selectedPatient.first_name.charAt(0).toUpperCase() +
                    selectedPatient.first_name.slice(1)
                  } ${selectedPatient.date_of_birth} (${selectedPatient.gender
                    .charAt(0)
                    .toUpperCase()})`}
              </div>
              {selectedPatient && `ID: ${selectedPatient.client_patient_uuid}`}
            </ToolbarGroup>
          )}
          <ToolbarSpacer />
          <ToolbarGroup>
            {activeDownloadsCount > 0 && (
              <StyledIconButton
                color="inherit"
                onClick={() => setIsActiveDownloadsModalOpen(true)}
              >
                <Badge badgeContent={activeDownloadsCount} color="error">
                  <DownloadIcon />
                </Badge>
              </StyledIconButton>
            )}
            <StyledIconButton
              ref={settingsAnchorRef}
              color="inherit"
              onClick={handleToggleSettings}
            >
              <Settings />
            </StyledIconButton>

            <Popper
              open={openSettings}
              anchorEl={settingsAnchorRef.current}
              role={undefined}
              transition
              disablePortal
              placement="bottom-start"
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    sx={{
                      backgroundColor: "#000000",
                      color: "#ffffff",
                      zIndex: 1301,
                    }}
                  >
                    <ClickAwayListener onClickAway={handleCloseSettings}>
                      <MenuList autoFocusItem={openSettings}>
                        <MenuItem
                          onClick={() => {
                            setOpenSettings(false);
                            setIsDeviceInfoOpen(true);
                          }}
                        >
                          Device Info
                        </MenuItem>
                        <MenuItem
                          ref={helpAnchorRef}
                          onMouseEnter={handleOpenHelp}
                          onMouseLeave={handleCloseHelp}
                        >
                          Help
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>

            <Popper
              open={openHelp}
              anchorEl={helpAnchorRef.current}
              placement="right-start"
              transition
              disablePortal
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper
                    sx={{
                      backgroundColor: "#000000",
                      color: "#ffffff",
                      zIndex: 1302,
                    }}
                    onMouseEnter={handleOpenHelp}
                    onMouseLeave={handleCloseHelp}
                  >
                    <MenuList>
                      <MenuItem
                        onClick={() => alert("Pyxis User Manual clicked")}
                      >
                        Pyxis User Manual
                      </MenuItem>
                      <MenuItem
                        onClick={() => alert("Customer Support clicked")}
                      >
                        Customer Support
                      </MenuItem>
                    </MenuList>
                  </Paper>
                </Grow>
              )}
            </Popper>
            <StyledIconButton color="inherit">
              <NotificationsIcon />
            </StyledIconButton>
            <StyledIconButton
              edge="end"
              color="inherit"
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
            >
              <AccountCircle />
            </StyledIconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#000000",
                  color: "#ffffff",
                },
              }}
            >
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          </ToolbarGroup>
        </Toolbar>
      </AppBar>
      <NewPatientModal
        open={isNewPatientModalOpen}
        onClose={() => setIsNewPatientModalOpen(false)}
      />
      <AboutDevice
        isOpen={isDeviceInfoOpen}
        onClose={() => setIsDeviceInfoOpen(false)}
      />
      <ActiveDownloadsModal
        open={isActiveDownloadsModalOpen}
        onClose={() => setIsActiveDownloadsModalOpen(false)}
      />
    </>
  );
};

export default TopToolbar;
